/**
 * web/src/components/speaker/NudgeReviewSheet.tsx
 * D1's "send with eyes" review sheet. Send now AND Preview both open this —
 * nothing leaves without it. Fetches a strictly read-only live render of ONE
 * planned nudge (useNudgePreview; nothing is claimed or cancelled server-side)
 * exactly as the scheduler would fire it right now:
 *   - From/To/Cc rows with an "inherited" note when the entry has no override
 *     (the recipients shown were resolved from the original email and will be
 *     re-resolved again at actual send time; From is the original's mailbox)
 *   - the rendered body, plus for the asset template the amber "outstanding
 *     right now" callout that matches the asset checklist
 *   - nothingOutstanding → a green "would be auto-cancelled" note, no Send
 *   - inboundActivity/inboundActivityError → an amber "they wrote first" (or
 *     "couldn't verify the inbox") warning; the primary button becomes
 *     "Send anyway" and passes force=true (skips ONLY that gate server-side)
 * 404/409 (entry gone / no longer sendable) renders an inline dead-end state.
 */
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { useNudgePreview } from '../../hooks/queries/useNudgePreview';
import type { useSendNudgeNow } from '../../hooks/mutations/useSendNudgeNow';
import { ApiClientError } from '../../lib/api';
import { Icon } from '../../lib/icons';
import { cn } from '../../lib/cn';
import { formatUtcIsoLocal } from '../../lib/dates';

function initialsOf(addr: string): string {
  const local = addr.split('@')[0] ?? '';
  return local.slice(0, 2).toUpperCase();
}

function RecipTag({ addr }: { addr: string }) {
  return (
    <span className="inline-flex items-center gap-[5px] rounded-full border border-border-strong bg-bg py-0.5 pl-[7px] pr-[9px] text-[11.5px]">
      <span className="grid h-[15px] w-[15px] place-items-center rounded-full bg-type-speaker-soft text-[8px] font-bold text-type-speaker">
        {initialsOf(addr)}
      </span>
      {addr}
    </span>
  );
}

/** Renders the review/send sheet for one planned nudge. Controlled `open`. */
export function NudgeReviewSheet({
  open,
  onOpenChange,
  trackedEmailId,
  index,
  recipientName,
  fromEmail,
  stepId,
  sendNow,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trackedEmailId: string;
  /** The plan index of the entry being reviewed. */
  index: number;
  recipientName: string;
  /** The mailbox the original email went out from (the From row). */
  fromEmail?: string | null;
  /** The send step this thread chases ('invite' | 'confirmation'). */
  stepId: string;
  sendNow: ReturnType<typeof useSendNudgeNow>;
}) {
  const preview = useNudgePreview(open ? trackedEmailId : undefined, index, open);
  const gone =
    preview.error instanceof ApiClientError &&
    (preview.error.status === 404 || preview.error.status === 409);
  const p = preview.data;
  const canSend = p != null && !p.nothingOutstanding;
  // The fire path would hold this nudge: someone already wrote (evidence
  // shown) or the mailbox couldn't be verified. Sending needs "Send anyway".
  const held = p != null && (p.inboundActivity != null || p.inboundActivityError != null);
  const isSending =
    sendNow.isPending &&
    sendNow.variables?.trackedEmailId === trackedEmailId &&
    sendNow.variables?.index === index;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[620px] max-w-[calc(100vw-48px)] p-0">
        {/* Head */}
        <div className="flex items-start gap-3 border-b border-border px-5 py-4">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-type-speaker-soft text-type-speaker">
            <Icon name="send" size={16} />
          </span>
          <div className="min-w-0">
            <DialogTitle className="text-[15px] font-bold -tracking-[0.01em]">
              Send Bump {index + 1} now
            </DialogTitle>
            <DialogDescription className="mt-px text-xs text-text-muted">
              {stepId === 'invite' ? 'Invitation follow-up' : 'Asset-request follow-up'} ·{' '}
              {recipientName}
            </DialogDescription>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Close"
            className="ml-auto grid h-7 w-7 place-items-center rounded-md text-text-subtle hover:bg-bg-hover"
          >
            <Icon name="x" size={14} />
          </button>
        </div>

        {/* As-of banner */}
        <div className="flex items-center gap-2 border-b border-accent-border bg-accent-soft px-5 py-2 text-[11.5px] text-accent-text">
          <Icon name="info" size={13} className="shrink-0" />
          Rendered as of now — recipients and the outstanding list are recomputed at send time.
        </div>

        {/* Body */}
        <div className="max-h-[60vh] overflow-y-auto">
          {preview.isPending ? (
            <div className="flex flex-col gap-2 px-5 py-6">
              <div role="status" className="mb-1 text-xs text-text-subtle">
                Rendering the follow-up exactly as it would send right now…
              </div>
              <div className="h-3 w-2/3 animate-pulse rounded bg-bg-muted" />
              <div className="h-3 w-full animate-pulse rounded bg-bg-muted" />
              <div className="h-3 w-5/6 animate-pulse rounded bg-bg-muted" />
            </div>
          ) : gone ? (
            <div className="flex items-start gap-2.5 px-5 py-6 text-[13px] text-text-muted">
              <Icon name="alert" size={15} className="mt-px shrink-0 text-waiting" />
              This bump is no longer sendable — it was sent, cancelled, or the plan changed. Close
              and check the timeline.
            </div>
          ) : preview.error || !p ? (
            <div className="flex flex-col items-start gap-2.5 px-5 py-6">
              <div className="text-[13px] text-failed">Couldn&apos;t load the preview.</div>
              <button
                type="button"
                onClick={() => void preview.refetch()}
                className="inline-flex items-center gap-1.5 rounded-md border border-border-strong px-2.5 py-1 text-[11px] font-medium transition-colors hover:bg-bg-hover"
              >
                <Icon name="retry" size={11} /> Try again
              </button>
            </div>
          ) : (
            <>
              {/* Inbound-activity warning — the scheduler would HOLD this bump */}
              {held && (
                <div className="flex items-start gap-2.5 border-b border-waiting-border bg-waiting-soft px-5 py-3 text-[12.5px] text-[#7a5a14]">
                  <Icon name="alert" size={14} className="mt-px shrink-0 text-waiting" />
                  <span>
                    {p.inboundActivity ? (
                      <>
                        <strong className="font-semibold">{p.inboundActivity.from}</strong> wrote{' '}
                        {formatUtcIsoLocal(p.inboundActivity.at)}
                        {p.inboundActivity.inThread ? ' on this thread' : ' to the sender'} — check
                        the thread before nudging.
                      </>
                    ) : (
                      <>
                        Couldn&apos;t verify the sender&apos;s inbox — automatic sending is held
                        until it can be checked.
                      </>
                    )}
                  </span>
                </div>
              )}

              {/* Envelope */}
              <div className="flex flex-col gap-[7px] border-b border-border bg-bg-soft px-5 py-3">
                {fromEmail && (
                  <div className="grid grid-cols-[52px_1fr] items-baseline gap-3 text-[12.5px]">
                    <span className="pt-px text-[10.5px] font-semibold uppercase tracking-[0.05em] text-text-subtle">
                      From
                    </span>
                    <span className="flex flex-wrap items-center gap-x-1.5 gap-y-[5px]">
                      <RecipTag addr={fromEmail} />
                      <span className="text-[11px] italic text-text-subtle">
                        same mailbox that sent the original
                      </span>
                    </span>
                  </div>
                )}
                <div className="grid grid-cols-[52px_1fr] items-baseline gap-3 text-[12.5px]">
                  <span className="pt-px text-[10.5px] font-semibold uppercase tracking-[0.05em] text-text-subtle">
                    To
                  </span>
                  <span className="flex flex-wrap items-center gap-x-1.5 gap-y-[5px]">
                    {p.to.map((a) => (
                      <RecipTag key={a} addr={a} />
                    ))}
                    {p.toInherited && (
                      <span className="text-[11px] italic text-text-subtle">
                        inherited from the original email
                      </span>
                    )}
                  </span>
                </div>
                {p.cc.length > 0 && (
                  <div className="grid grid-cols-[52px_1fr] items-baseline gap-3 text-[12.5px]">
                    <span className="pt-px text-[10.5px] font-semibold uppercase tracking-[0.05em] text-text-subtle">
                      Cc
                    </span>
                    <span className="flex flex-wrap items-center gap-x-1.5 gap-y-[5px]">
                      {p.cc.map((a) => (
                        <RecipTag key={a} addr={a} />
                      ))}
                      {p.ccInherited && (
                        <span className="text-[11px] italic text-text-subtle">inherited</span>
                      )}
                    </span>
                  </div>
                )}
                <div className="grid grid-cols-[52px_1fr] items-baseline gap-3 text-xs text-text-muted">
                  <span className="pt-px text-[10.5px] font-semibold uppercase tracking-[0.05em] text-text-subtle">
                    Thread
                  </span>
                  <span>
                    <Icon name="mail" size={11} className="mr-1 inline-block align-[-1px]" />
                    Lands on <strong className="font-semibold text-text">{p.subject}</strong>
                  </span>
                </div>
              </div>

              {/* Subject + body */}
              <div className="px-5 pb-1 pt-3 text-[15px] font-bold">{p.subject}</div>
              {p.nothingOutstanding ? (
                <div className="mx-5 mb-4 mt-2 flex items-start gap-2.5 rounded-lg border border-completed-border bg-completed-soft px-3.5 py-3 text-[12.5px] text-completed">
                  <Icon name="check" size={14} className="mt-px shrink-0" />
                  Everything&apos;s in — this bump would be auto-cancelled instead of sent.
                </div>
              ) : (
                <pre className="whitespace-pre-wrap px-5 pb-4 pt-1.5 font-sans text-[13.5px] leading-[1.65] text-text">
                  {p.bodyText ?? ''}
                </pre>
              )}

              {/* Asset template: what this bump chases, matching the checklist */}
              {p.outstanding != null && p.outstanding.length > 0 && (
                <div className="mx-5 mb-4 rounded-lg border border-waiting-border bg-waiting-soft px-[13px] py-[11px]">
                  <div className="mb-2 flex items-center gap-[7px] text-[11px] font-bold uppercase tracking-[0.05em] text-waiting">
                    <Icon name="upload" size={12} /> Outstanding right now — matches the asset
                    checklist
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {p.outstanding.map((a) => (
                      <span
                        key={a.key}
                        className="inline-flex items-center gap-1.5 rounded-full border border-waiting-border bg-bg py-[3px] pl-2 pr-2.5 text-xs font-medium text-[#7a5a14]"
                      >
                        <Icon name="paperclip" size={11} /> {a.label}
                      </span>
                    ))}
                  </div>
                  {p.satisfied != null && p.satisfied.length > 0 && (
                    <div className="mt-2 text-[11px] text-[#7a5a14]">
                      Already in: {p.satisfied.map((a) => a.label).join(', ')} — dropped from the
                      ask.
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Foot */}
        <div className="flex items-center gap-2.5 border-t border-border bg-bg-soft px-5 py-3">
          {canSend && (
            <span className="text-[11.5px] text-text-subtle">
              Sends immediately to {p.to.length} recipient{p.to.length === 1 ? '' : 's'} on the
              tracked thread.
            </span>
          )}
          <span className="flex-1" />
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-md border border-border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-bg-hover"
          >
            Cancel
          </button>
          {canSend && !gone && (
            <button
              type="button"
              disabled={isSending || sendNow.isPending}
              onClick={() =>
                sendNow.mutate(
                  { trackedEmailId, index, force: held },
                  { onSuccess: () => onOpenChange(false) },
                )
              }
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-white',
                'transition-colors disabled:opacity-50',
                held ? 'bg-waiting hover:bg-waiting/90' : 'bg-accent hover:bg-accent-hover',
              )}
            >
              <Icon name="send" size={12} />{' '}
              {isSending ? 'Sending…' : held ? 'Send anyway' : 'Send now'}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
